<template>
  <div>
    <b-row>
      <b-col sm="12">
        <b-card>
          <b-row class="align-items-end">
            <!-- Date Picker -->
            <b-col sm="4" md="3" class="mb-2 mb-sm-0">
              <h5 class="font-weight-bold">{{ $t('Date') }}</h5>
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="CalendarIcon" class="text-muted" />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="selectedDate"
                  class="form-control"
                  style="cursor: pointer; background-color: #fff;"
                />
              </b-input-group>
            </b-col>

            <!-- Time Range Picker (integrated) -->
            <b-col sm="6" md="5" class="mb-2 mb-sm-0">
              <h5 class="font-weight-bold">{{ $t('Time Range') }}</h5>
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="ClockIcon" class="text-muted" />
                </b-input-group-prepend>
                
                <flat-pickr
                  v-model="timeStart"
                  :config="timeConfig"
                  class="form-control text-center"
                  style="cursor: pointer; background-color: #fff;"
                  @on-change="validateTimeRange"
                />
                
                <b-input-group-text class="border-left-0 border-right-0 bg-transparent px-1">
                  {{ $t('to') }}
                </b-input-group-text>
                
                <flat-pickr
                  v-model="timeEnd"
                  :config="timeConfig"
                  class="form-control text-center"
                  style="cursor: pointer; background-color: #fff;"
                  @on-change="validateTimeRange"
                />
              </b-input-group>
            </b-col>

            <!-- Btn Send -->
            <b-col sm="2" md="4" class="text-sm-right mt-2 mt-sm-0">
              <b-button :disabled="loading" variant="primary" type="button" @click="composeReport" class="w-100 w-sm-auto">
                <feather-icon icon="SearchIcon" class="mr-50" />
                {{ $t('Search') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Map Section -->
    <b-row>
      <b-col sm="12">
        <b-card>
          <b-row>
            <b-col sm="12">
              <l-map ref="harvestMap" :zoom="zoom" :center="center" :key="mapKey" class="geo-map-container">
                <l-tile-layer :url="currentUrl"></l-tile-layer>
                <l-control position="topright">
                  <b-button-group size="sm">
                    <b-button variant="light" :pressed="selectedLayer === 'map'" @click="chooseLayer('map')">{{ $t('Map') }}</b-button>
                    <b-button variant="light" :pressed="selectedLayer === 'ground'" @click="chooseLayer('ground')">{{ $t('Ground') }}</b-button>
                    <b-button variant="light" :pressed="selectedLayer === 'hybrid'" @click="chooseLayer('hybrid')">{{ $t('Hybrid') }}</b-button>
                  </b-button-group>
                </l-control>
                <l-control position="topright">
                  <b-button @click="showTelemetry = !showTelemetry" variant="primary" size="sm">
                    <span v-show="!showTelemetry">{{ $t('Pin Telemetry') }}</span>
                    <span v-show="showTelemetry">{{ $t('Hide Telemetry') }}</span>
                  </b-button>
                </l-control>

                <l-marker v-for="(asset, i) in assetsData" :key="i" :lat-lng="[asset.lat, asset.lng]" @mouseover="hoveringAssetIndex = i" @mouseout="hoveringAssetIndex = -1">
                  <l-icon class-name="pin">
                    <div class="marker-content">
                      <span v-if="i === 0" class="marker-icon">🚩</span>
                      <span v-else-if="i === assetsData.length - 1" class="marker-icon">🏁</span>
                      <span v-else class="marker-index"></span>
                    </div>
                  </l-icon>
                  <l-tooltip v-if="showTelemetry || hoveringAssetIndex === i" :options="{ permanent: true, interactive: false }">
                    <div>
                      <div>⏰ {{ $t('Time') }}: <strong>{{ new Date(asset.dateTime).toLocaleTimeString().substring(0, 5) }}</strong></div>
                      <div v-for="(value, key) in asset.data" :key="key">
                        <span><strong>{{ key }}: </strong> {{ value }}</span>
                      </div>
                    </div>
                  </l-tooltip>
                </l-marker>
                <l-polyline :opacity="0.6" :lat-lngs="latlngs" color="#4285f4"></l-polyline>
              </l-map>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Loading Modal -->
    <b-modal ref="modalReport" size="sm" centered hide-header hide-footer no-close-on-esc no-close-on-backdrop hide-header-close>
      <div class="mx-3 text-center py-2">
        <h4 class="mb-2">{{ $t('Preparing report') }}...</h4>
        <b-spinner variant="primary" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BCard,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupText,
  BSpinner,
  BModal,
  BButtonGroup
} from 'bootstrap-vue'
import { ref, computed, watch, onUnmounted, nextTick, getCurrentInstance } from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import queriesStoreModule from '../queriesStoreModule'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue"
import flatPickr from 'vue-flatpickr-component'
import "leaflet/dist/leaflet.css"
import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPolyline,
  LTooltip,
  LControl,
} from "vue2-leaflet"

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupText,
    BSpinner,
    BModal,
    BButtonGroup,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPolyline,
    LTooltip,
    LControl,
    flatPickr
  },
  setup() {
    const QUERIES_APP_STORE_MODULE_NAME = 'app-queries'
    const instance = getCurrentInstance()
    const assetId = ref(router.currentRoute.params.id)
    const $t = instance?.proxy?.$t ? instance.proxy.$t.bind(instance.proxy) : (v) => v

    // Store Module Registration
    if (!store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) {
      store.registerModule(QUERIES_APP_STORE_MODULE_NAME, queriesStoreModule)
    }
    onUnmounted(() => {
      if (store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(QUERIES_APP_STORE_MODULE_NAME)
      }
    })

    // UI States
    const loading = ref(false)
    const showTelemetry = ref(false)
    const hoveringAssetIndex = ref(-1)
    
    // Map States
    const zoom = ref(4)
    const mapKey = ref('harvestmap')
    const center = ref([-12.4131269, -55.5261856])
    const assetsData = ref([])
    const latlngs = ref([])
    const harvestMap = ref(null)
    
    // Layers
    const selectedLayer = ref(localStorage.selectedLayer || "map")
    const currentUrl = computed(() => {
      if (selectedLayer.value === "hybrid") {
        return "https://2.aerial.maps.ls.hereapi.com/maptile/2.1/maptile/newest/hybrid.day/{z}/{x}/{y}/256/png8?lg=por&apiKey=U7wvT2IU0rFTeGwMoIpdf7JaQx5QH74SyhG4qiHy7Q8"
      }
      if (selectedLayer.value === "ground") {
        return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
      }
      return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    })

    const chooseLayer = (layer) => {
      selectedLayer.value = layer
      localStorage.selectedLayer = layer
    }

    // Forms and Validations
    const today = new Date()
    // Helper to format local date to YYYY-MM-DD
    const pad = (n) => n < 10 ? '0' + n : n
    const selectedDate = ref(`${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`)
    const timeStart = ref('00:00')
    const timeEnd = ref('23:59')

    const timeConfig = ref({
      enableTime: true,
      noCalendar: true,
      dateFormat: 'H:i',
      time_24hr: true
    })

    const showToast = (title, text, variant = 'info', icon = 'InfoIcon') => {
      if (instance?.proxy?.$toast) {
        instance.proxy.$toast({
          component: ToastificationContent,
          props: { title, text, icon, variant },
        })
      }
    }

    const validateTimeRange = () => {
      if (!timeStart.value || !timeEnd.value) return
      
      const startMinutes = parseInt(timeStart.value.split(':')[0]) * 60 + parseInt(timeStart.value.split(':')[1])
      const endMinutes = parseInt(timeEnd.value.split(':')[0]) * 60 + parseInt(timeEnd.value.split(':')[1])
      
      if (startMinutes >= endMinutes) {
        showToast($t('Invalid Time Range'), $t('The end time must be after the start time.'), 'warning', 'AlertTriangleIcon')
        timeStart.value = '00:00'
        timeEnd.value = '23:59'
      }
    }

    // Clear Tooltips Fix
    const clearTooltip = () => {
      const tooltips = document.querySelectorAll('.leaflet-tooltip')
      tooltips.forEach((tooltip) => {
        tooltip.style.display = 'none'
      })
    }

    watch(showTelemetry, (newVal) => {
      if (!newVal) clearTooltip()
    })
    
    watch(hoveringAssetIndex, (newIndex) => {
      if (newIndex === -1 && !showTelemetry.value) {
        clearTooltip()
      }
    })

    // Data Fetching
    const modalReport = ref(null)
    const showModalReport = () => modalReport.value?.show()
    const hideModalReport = () => modalReport.value?.hide()

    const formatDateToIso = (dateStr, hourStr) => {
      return `${dateStr}T${hourStr}`
    }

    const composeReport = async () => {
      validateTimeRange() // ensures valid range before sending

      let cf = false
      let page = 1
      assetsData.value = []
      latlngs.value = []
      loading.value = true
      showModalReport()

      try {
        while (!cf) {
          const response = await store.dispatch('app-queries/searchTelemetry', {
            assetId: assetId.value,
            initalDate: formatDateToIso(selectedDate.value, timeStart.value),
            finalDate: formatDateToIso(selectedDate.value, timeEnd.value),
            page: page
          })

          if (response?.data?.data?.length > 0) {
            response.data.data.forEach(r => {
              if (r.lat && r.lat !== 0 && r.lng && r.lng !== 0) {
                assetsData.value.push(r)
                latlngs.value.push([r.lat, r.lng])
              }
            })
          } else {
            cf = true
            loading.value = false
            hideModalReport()

            if (latlngs.value.length > 0) {
              nextTick(() => {
                if (harvestMap.value?.mapObject) {
                  harvestMap.value.mapObject.fitBounds(
                    latlngs.value.map(m => [m[0], m[1]])
                  )
                }
              })
            } else {
              showToast($t('No results found'), $t('No data found for the selected period.'), 'info')
            }
          }
          page++
        }
      } catch (e) {
        assetsData.value = []
        latlngs.value = []
        cf = true
        loading.value = false
        hideModalReport()
        showToast($t('Error'), $t('An unexpected error occurred while fetching telemetry.'), 'danger', 'AlertCircleIcon')
      }
    }

    return {
      // refs
      assetId,
      loading,
      showTelemetry,
      hoveringAssetIndex,
      zoom,
      mapKey,
      center,
      assetsData,
      latlngs,
      harvestMap,
      selectedLayer,
      currentUrl,
      selectedDate,
      timeStart,
      timeEnd,
      timeConfig,
      modalReport,
      
      // methods
      chooseLayer,
      validateTimeRange,
      composeReport,
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/map-leaflet.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.pin {
  background-color: #4285f4 !important;
  border-radius: 50%;
  text-align: center;
  width: 16px !important;
  height: 16px !important;
}

.marker-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 0;
}

.marker-icon {
  color: #4285f4;
  font-size: 20px;
}

.marker-index {
  color: #4285f4;
}

.geo-map-container {
  &.leaflet-container {
    height: 55vh !important;
    border-radius: 0.358rem;
  }
}
</style>